// src/components/home/MobileDiscover.js
// Parcours mobile facon application (Groupon / Deliveroo) : pas de grande
// banniere de presentation, on entre directement dans le catalogue.
//
//   1) RAIL DE CATEGORIES — pastilles rondes en defilement horizontal.
//      Sur telephone, une grille de categories coute une pleine hauteur d'ecran ;
//      un rail coute 120 px et laisse le pouce balayer. C'est le geste attendu
//      sur mobile, pas le defilement vertical.
//   2) BANNIERE PROMO — une carte large a la fois, balayage au doigt avec
//      accroche CSS (scroll-snap). Pas de minuteur : sur mobile, une rotation
//      automatique arrache la carte des doigts au moment ou l'on veut cliquer.
//
// Ce bloc REMPLACE, sous 768 px, la banniere d'accueil + les 4 tuiles + le
// carrousel de categories. Au-dela, ce sont les blocs desktop qui s'affichent.
//
// Maxula — AbodyStudio Limited

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronRight, FiChevronLeft, FiSearch } from 'react-icons/fi';

import { adAPI, categoryAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import { renderCategoryIcon } from '../../utils/categoryIcons';
import { localizedName } from '../../utils/localizedName';
import { useLanguage } from '../../i18n/LanguageContext';

const PLACEMENT = 'home_tiles';
const MAX_PROMOS = 6;

// Memes couleurs que les tuiles desktop, pour que le mobile reste la meme marque.
const COLORS = ['124, 58, 237', '225, 29, 123', '13, 148, 136', '234, 88, 12'];

const CSS = `
.md-rail {
  display: flex; gap: 14px; overflow-x: auto; overflow-y: hidden;
  scroll-snap-type: x proximity; -webkit-overflow-scrolling: touch;
  padding: 2px 16px 6px;
}
/* Barre de defilement masquee : sur mobile elle ne sert a rien et grignote
   quelques pixels de hauteur sous Android. */
.md-rail::-webkit-scrollbar { display: none; }
.md-rail { scrollbar-width: none; -ms-overflow-style: none; }
.md-rail-item { scroll-snap-align: start; flex: 0 0 auto; width: 68px; text-align: center; }
.md-avatar {
  width: 64px; height: 64px; border-radius: 9999px; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.10);
  transition: transform 200ms ease-out;
}
.md-rail-item:active .md-avatar { transform: scale(0.92); }
.md-label {
  margin-top: 6px; font-size: 11px; line-height: 1.25; font-weight: 600; color: #374151;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.md-promos {
  display: flex; gap: 12px; overflow-x: auto; overflow-y: hidden;
  scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch;
  padding: 0 16px 4px;
}
.md-promos::-webkit-scrollbar { display: none; }
.md-promos { scrollbar-width: none; -ms-overflow-style: none; }
/* 86% de largeur et non 100% : le bord de la carte suivante reste visible.
   C'est ce qui indique au visiteur qu'il peut balayer — sans ce debord, la
   plupart des gens ne devinent pas qu'il y a une suite. */
.md-promo { scroll-snap-align: center; flex: 0 0 86%; height: 150px; position: relative;
  border-radius: 16px; overflow: hidden; box-shadow: 0 6px 18px rgba(0,0,0,0.12); }
/* Clamp maison plutot que la classe utilitaire line-clamp-2 : elle n'existe en
   coeur de Tailwind qu'a partir de la 3.3 et demandait un plugin avant. */
.md-clamp2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
@keyframes mdFade { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.md-in { animation: mdFade 420ms ease-out backwards; }
@media (prefers-reduced-motion: reduce) {
  .md-in { animation: none; }
  .md-rail-item:active .md-avatar { transform: none; }
}
`;

const clean = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const low = s.toLowerCase();
  if (low === 'null' || low === 'undefined' || low === 'nan') return null;
  return s;
};

const isInternal = (url) => typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');

// Comme AdBanner : un lien absolu saisi en local ne doit pas envoyer le
// visiteur sur localhost une fois en production.
const toRoute = (raw, fallback = '/deals') => {
  const s = clean(raw);
  if (!s) return fallback;
  if (isInternal(s)) return s;
  try {
    const origin = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost';
    const u = new URL(s, origin);
    const host = (u.hostname || '').toLowerCase();
    const local = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local');
    const same = typeof window !== 'undefined' && window.location &&
      host === String(window.location.hostname || '').toLowerCase();
    if (local || same) return u.pathname + u.search + u.hash || fallback;
    return s;
  } catch (e) {
    return s;
  }
};

const MobileDiscover = ({ className = '' }) => {
  const { t, lang, isRTL } = useLanguage();
  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promoIndex, setPromoIndex] = useState(0);
  const promosRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const [adRes, catRes] = await Promise.allSettled([
        adAPI.getActive(PLACEMENT),
        categoryAPI.getAll(),
      ]);
      if (!mounted) return;
      if (adRes.status === 'fulfilled') {
        const list = adRes.value?.data?.data;
        setAds(Array.isArray(list) ? list : []);
      }
      if (catRes.status === 'fulfilled') {
        const payload = catRes.value?.data;
        setCategories(
          Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : []
        );
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Categories a afficher : celles qui ont au moins une annonce, les plus
  // fournies d'abord. Une pastille qui ouvre une liste vide est pire qu'une
  // pastille en moins.
  const cats = useMemo(
    () =>
      [...categories]
        .filter((c) => c && (c.isActive === undefined || c.isActive))
        .filter((c) => Number(c?.dealsCount || 0) > 0)
        .sort((a, b) => Number(b?.dealsCount || 0) - Number(a?.dealsCount || 0)),
    [categories]
  );

  const promos = useMemo(() => {
    const fromAds = ads
      .filter((a) => clean(a?.title) || clean(a?.image))
      .map((a) => ({
        key: `ad-${a._id ?? a.id}`,
        adId: a._id ?? a.id,
        title: clean(a.title) || '',
        subtitle: clean(a.advertiser) || clean(a.badge) || '',
        image: clean(a.image) ? normalizeImagePath(a.image) : null,
        to: toRoute(a.link),
        cta: clean(a.ctaLabel) || t('home.tilesCta'),
      }));

    const used = new Set(fromAds.map((x) => String(x.title).toLowerCase()));
    const fromCats = cats
      .filter((c) => !used.has(String(localizedName(c, lang)).toLowerCase()))
      .map((c) => ({
        key: `cat-${c._id ?? c.id}`,
        adId: null,
        title: localizedName(c, lang),
        subtitle: `${Number(c.dealsCount || 0)} ${t('home.tilesDeals')}`,
        image: clean(c.image) ? normalizeImagePath(c.image) : null,
        to: `/deals?category=${c.slug ?? c._id ?? c.id}`,
        cta: t('home.tilesCta'),
      }));

    // Les publicites d'abord : ce sont des emplacements payes, ils doivent
    // apparaitre sans que le visiteur ait a balayer.
    return [...fromAds, ...fromCats].slice(0, MAX_PROMOS);
  }, [ads, cats, lang, t]);

  // Pastilles calculees a partir du defilement reel, et non d'un compteur
  // interne : le visiteur balaie au doigt, l'indicateur doit suivre SON geste.
  const onPromoScroll = () => {
    const el = promosRef.current;
    if (!el || !promos.length) return;
    const step = el.scrollWidth / promos.length;
    const i = Math.round(Math.abs(el.scrollLeft) / (step || 1));
    setPromoIndex(Math.min(promos.length - 1, Math.max(0, i)));
  };

  if (!cats.length && !promos.length) return null;

  const Chevron = isRTL ? FiChevronLeft : FiChevronRight;

  return (
    <section className={`bg-white pt-3 pb-5 ${className}`}>
      <style>{CSS}</style>

      {/* Accroche courte a la place de la banniere : une phrase, un lien.
          La grande banniere consommait 330 px pour dire la meme chose. */}
      <div className="px-4 flex items-end justify-between">
        <div>
          <h1 className="text-lg font-extrabold text-gray-900 leading-tight">
            {t('home.mobileTitle')}
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">{t('home.mobileSub')}</p>
        </div>
        <Link
          to="/deals"
          className="shrink-0 inline-flex items-center gap-1 text-[13px] font-bold text-[#e11d7b]"
        >
          {t('home.seeAllDeals')} <Chevron size={15} />
        </Link>
      </div>

      {/* 1) RAIL DE CATEGORIES */}
      {cats.length ? (
        <div className="md-rail md-in mt-3" role="list">
          {cats.map((c, i) => {
            const rgb = COLORS[i % COLORS.length];
            const img = clean(c.image) ? normalizeImagePath(c.image) : null;
            return (
              <Link
                key={c._id ?? c.id}
                to={`/deals?category=${c.slug ?? c._id ?? c.id}`}
                className="md-rail-item"
                role="listitem"
              >
                <div className="md-avatar" style={{ backgroundColor: `rgba(${rgb}, 0.12)` }}>
                  {img ? (
                    <img src={img} alt="" aria-hidden="true" className="h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <span style={{ color: `rgb(${rgb})`, lineHeight: 0 }}>
                      {renderCategoryIcon(c.icon, 26, '')}
                    </span>
                  )}
                </div>
                <div className="md-label">{localizedName(c, lang)}</div>
              </Link>
            );
          })}

          {/* Derniere pastille : acces a la liste complete. Le rail ne montre que
              ce qui a des offres, il faut une porte de sortie vers le reste. */}
          <Link to="/deals" className="md-rail-item" role="listitem">
            <div className="md-avatar bg-gray-100">
              <FiSearch size={24} className="text-gray-500" />
            </div>
            <div className="md-label">{t('home.mobileAllCategories')}</div>
          </Link>
        </div>
      ) : null}

      {/* 2) BANNIERE PROMO BALAYABLE */}
      {promos.length ? (
        <>
          <div
            className="md-promos md-in mt-4"
            ref={promosRef}
            onScroll={onPromoScroll}
            style={{ animationDelay: '80ms' }}
          >
            {promos.map((p, i) => {
              const rgb = COLORS[i % COLORS.length];
              const onClick = () => {
                if (!p.adId) return;
                try { adAPI.trackClick(p.adId); } catch (e) {}
              };

              const body = (
                <>
                  {p.image ? (
                    <img src={p.image} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
                  ) : (
                    <div
                      className="absolute inset-0"
                      style={{ backgroundImage: `linear-gradient(140deg, rgb(${rgb}) 0%, rgba(${rgb}, 0.65) 100%)` }}
                    />
                  )}
                  {/* Voile lateral et non vertical : sur une carte large et
                      basse, le texte est a gauche, pas en bas. */}
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `linear-gradient(${isRTL ? 270 : 90}deg, rgba(${rgb}, 0.92) 0%, rgba(${rgb}, 0.62) 45%, rgba(0,0,0,0.18) 100%)`,
                    }}
                  />
                  <div className="relative z-10 h-full flex flex-col justify-center px-4 max-w-[70%]">
                    <h3
                      className="text-white font-extrabold text-[17px] leading-tight"
                      style={{ textShadow: '0 1px 8px rgba(0,0,0,0.35)' }}
                    >
                      {p.title}
                    </h3>
                    {p.subtitle ? (
                      <p className="md-clamp2 text-white/90 text-[11px] mt-1">{p.subtitle}</p>
                    ) : null}
                    <span className="mt-2 inline-flex w-fit items-center gap-1 rounded-full bg-white px-3 py-1 text-[11px] font-bold"
                      style={{ color: `rgb(${rgb})` }}
                    >
                      {p.cta} <Chevron size={12} />
                    </span>
                  </div>
                </>
              );

              return isInternal(p.to) ? (
                <Link key={p.key} to={p.to} onClick={onClick} className="md-promo" title={p.title}>
                  {body}
                </Link>
              ) : (
                <a key={p.key} href={p.to} target="_blank" rel="noreferrer" onClick={onClick} className="md-promo" title={p.title}>
                  {body}
                </a>
              );
            })}
          </div>

          {promos.length > 1 && (
            <div className="mt-2 flex justify-center gap-1.5">
              {promos.map((p, i) => (
                <span
                  key={p.key}
                  className={`h-1.5 rounded-full transition-all ${
                    i === promoIndex ? 'w-4 bg-[#e11d7b]' : 'w-1.5 bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </>
      ) : null}
    </section>
  );
};

export default MobileDiscover;
